import React from 'react';
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useAiDraftEnabled, useDraftReply } from './useDraftReply';
import { SnoozeButton, SnoozeFailure } from './SnoozeButton';

type DraftReply = ReturnType<typeof useDraftReply>;

const TICKET_ACTION_SX = {
  color: '#7D818C',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  padding: '6px 10px',
} as const;

interface IReplyComposerProps {
  reply: DraftReply;
  /**
   * The family the controls act on. The composer outlives a selection
   * change, so the snooze control is keyed by it: a snooze still in flight
   * for the previous family must not open its retry under this one.
   */
  memberUserId?: string;
  onCloseTicket?: () => void;
  /** Resolves to why the snooze did not take, else null. */
  onSnooze?: (until: string) => Promise<SnoozeFailure | null>;
  closingTicket?: boolean;
  disabled?: boolean;
}

/**
 * The inbox pane's inline composer, per Sam's design: a suggested-reply
 * header over an editable draft, then Send / Copy / Snooze / Close ticket.
 *
 * Send is deliberately inert. The backend has no outbound reply path — the
 * only endpoint is POST /admin/users/:userId/conversations/draft-reply — so
 * replies still leave from Intercom and the care rep pastes the draft there.
 * Real send arrives with the Phase 2a native send pilot; the control holds
 * its designed position until then rather than moving the layout later.
 */
export const ReplyComposer: React.FC<IReplyComposerProps> = ({
  reply,
  memberUserId,
  onCloseTicket,
  onSnooze,
  closingTicket,
  disabled,
}) => {
  const { draft, setDraft, drafting, draftError, copied, copyError } = reply;
  const aiDraftEnabled = useAiDraftEnabled();

  return (
    <Stack
      sx={{
        borderTop: '1px solid #DBD8D8',
        backgroundColor: '#FFFFFF',
        padding: '16px',
        gap: '10px',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <AutoAwesomeOutlinedIcon
            sx={{ color: '#356766', fontSize: '18px' }}
          />
          <Typography
            sx={{ fontWeight: 700, fontSize: '14px', color: '#356766' }}
          >
            Suggested reply
          </Typography>
        </Stack>
        {aiDraftEnabled && (
          <Button
            size="small"
            disabled={disabled || drafting}
            onClick={reply.requestDraft}
            sx={{ color: '#356766', textTransform: 'none', fontWeight: 600 }}
          >
            {draft === null && !draftError ? 'Generate' : 'Regenerate'}
          </Button>
        )}
      </Stack>

      {drafting ? (
        <Stack alignItems="center" paddingY="24px">
          <CircularProgress size={26} sx={{ color: '#356766' }} />
        </Stack>
      ) : (
        <TextField
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          disabled={disabled}
          value={draft || ''}
          placeholder={
            aiDraftEnabled
              ? 'Generate a suggested reply, or write your own.'
              : 'Write a reply to copy into Intercom.'
          }
          onChange={(e) => setDraft(e.target.value)}
        />
      )}

      {!!draftError && (
        <Typography sx={{ color: '#B3261E', fontSize: '14px' }}>
          {draftError}
        </Typography>
      )}
      {!!copyError && (
        <Typography sx={{ color: '#B3261E', fontSize: '14px' }}>
          {copyError}
        </Typography>
      )}

      <Stack direction="row" alignItems="center" gap={1}>
        <Tooltip title="Replies still go out from Intercom. Sending from here arrives with native messaging.">
          {/* A disabled button swallows pointer events, so the tooltip needs
              a wrapper that still receives them. */}
          <span>
            <Button
              variant="contained"
              disabled
              sx={{
                backgroundColor: '#213B54',
                borderRadius: '20px',
                paddingX: '24px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Send
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          disabled={!draft}
          onClick={reply.copyDraft}
          sx={{
            borderRadius: '20px',
            paddingX: '20px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#213B54',
            borderColor: '#DBD8D8',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Stack direction="row" gap={0.5} sx={{ marginLeft: 'auto' }}>
          {!!onSnooze && (
            <SnoozeButton
              key={memberUserId}
              disabled={disabled || closingTicket}
              onSnooze={onSnooze}
              sx={TICKET_ACTION_SX}
            />
          )}
          {!!onCloseTicket && (
            <Button
              disabled={disabled || closingTicket}
              onClick={onCloseTicket}
              sx={TICKET_ACTION_SX}
            >
              Close ticket
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
